/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import React, {useState} from 'react'
import {ZIndexProps} from './model'
import {Button} from '@targetprocess/button'
import {Popover} from '@targetprocess/popover'
import classNames from 'classnames'
import {ChevronIcon, WarningIcon, InfoIcon} from '@targetprocess/icons'
import {ButtonColorDefault} from '@targetprocess/global-constants'
import {GeneralColors} from '@targetprocess/colors'
import {LogLevel} from '../LogsList'
import styles from '../style/index.css'
import intl from '../utils/intl/intl'

export interface LevelHeaderProps extends ZIndexProps {
  level?: LogLevel
  levelOptions?: LogLevel[]
  onLevelChange?: (level?: LogLevel) => void
}

const getLevelTitle = (level: LogLevel) => {
  switch (level) {
    case LogLevel.Debug:
      return intl.formatMessage('Debug')
    case LogLevel.Error:
      return intl.formatMessage('Error')
    case LogLevel.Info:
      return intl.formatMessage('Info')
    case LogLevel.Warn:
      return intl.formatMessage('Warning')
    default:
      return ''
  }
}

const LevelsList = ({onLevelChange, levelOptions}: LevelHeaderProps) => (
  <div className={classNames(styles['popover__item'])}>
    <div onClick={() => onLevelChange && onLevelChange(undefined)}>
      {' '}
      {intl.formatMessage('All levels')}
    </div>
    {levelOptions &&
      levelOptions.map &&
      levelOptions.map((level) => (
        <div onClick={() => onLevelChange && onLevelChange(level)} key={level}>
          {level}
        </div>
      ))}
  </div>
)
LevelsList.displayName = 'LevelsList'

const LevelButtonHeader = ({onClick}) => <div onClick={onClick} />
LevelButtonHeader.displayName = 'LevelButtonHeader'

export const LevelHeader = (props: LevelHeaderProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const {onLevelChange, levelOptions, level: currentLevel, popoverZIndex} = props

  return (
    <Popover
      zIndex={popoverZIndex}
      getPopoverPosition={'bottom'}
      isOpen={isPopoverOpen}
      body={
        <Popover.Body noPadding>
          <div className={classNames(styles['popover'])}>
            <div
              className={classNames(styles['popover__item'])}
              onClick={() => {
                // tslint:disable-next-line
                onLevelChange && onLevelChange(undefined)
                setIsPopoverOpen(false)
              }}
            >
              {intl.formatMessage('All levels')}
            </div>
            {levelOptions &&
              levelOptions.map((level) => (
                <div
                  className={classNames(styles['popover__item'])}
                  onClick={() => {
                    // tslint:disable-next-line
                    onLevelChange && onLevelChange(level)
                    setIsPopoverOpen(false)
                  }}
                  key={level}
                >
                  <div className={classNames(styles['popover__item__icon-container'])}>
                    <LevelIcon level={level} />
                  </div>
                  {getLevelTitle(level)}
                </div>
              ))}
          </div>
        </Popover.Body>
      }
    >
      <div
        className={classNames(styles['log__header-button-container'])}
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
      >
        {currentLevel ? (
          <div
            className={classNames(styles['log__header__name'], styles['log__header__name--active'])}
          >
            <LevelIcon level={currentLevel} />
          </div>
        ) : (
          <Button icon={<ChevronIcon color={ButtonColorDefault} />} noBackground />
        )}
      </div>
    </Popover>
  )
}
LevelHeader.displayName = 'LevelHeader'

const LevelIcon = ({level}: {level: LogLevel}) => (
  <React.Fragment>
    {level === LogLevel.Warn && <WarningIcon />}
    {level === LogLevel.Info && <InfoIcon />}
    {level === LogLevel.Debug && <InfoIcon />}
    {level === LogLevel.Error && <WarningIcon color={GeneralColors.Danger} />}
  </React.Fragment>
)

export const LevelRow = ({item: {level}}: {item: {level: LogLevel}}) => (
  <div className={classNames(styles['log__cell-item__icon'])}>
    <LevelIcon level={level} />
  </div>
)

export const InfoLevelIcon = () => <LevelRow item={{level: LogLevel.Info}} />
